import { useCallback, useEffect, useRef, useState } from 'react';
import { callUIAPI } from '@utils/apiUtils';
import { ALL_AGENTS, AGENT_TYPE_MAP, SCAN_OWNED_LANE_KEYS, PERSONA_LANE_KEY, agentByKey, type AgentLaneState, type BuildSubStep } from './agentConfig';

const MAX_SUBSTEPS = 6;

function appendSubStep(steps: BuildSubStep[] | undefined, laneKey: string, index: number, text: string): BuildSubStep[] {
  const prior = (steps ?? []).map(s => ({ ...s, done: true }));
  prior.push({ key: `${laneKey}-${index}`, text, done: false });
  return prior.slice(-MAX_SUBSTEPS);
}
import { toStreamError, type StreamError } from './errors';
import { useLaneCreep } from './useLaneCreep';
import { useScanConnection } from './useScanConnection';
import { computeStepProgress, deriveStepIcon, parseStreamMessage } from './scanStreamUtils';

const SCAN_OWNED = new Set(SCAN_OWNED_LANE_KEYS);

async function fetchPersistedScan(scanDbId?: number | null): Promise<{ result: unknown } | null> {
  if (!scanDbId) return null;
  try {
    const res = await callUIAPI('scans/latest', null, 'get', true) as Record<string, unknown>;
    const record = res?.data as Record<string, unknown> | undefined;
    if (!record || record.id !== scanDbId) return null;
    if (record.status !== 'completed' || !record.result) return null;
    return { result: record.result };
  } catch {
    return null;
  }
}

function initialStates(): Record<string, AgentLaneState> {
  return Object.fromEntries(
    ALL_AGENTS.filter(a => SCAN_OWNED.has(a.key))
      .map(a => [a.key, { phase: 'queued', progress: 0, message: '' }]),
  );
}

interface UseScanAgentStreamOptions {
  scanId?: string | null;
  scanDbId?: number | null;
  companyName: string;
  websiteUrl?: string | null;
  businessType?: string | null;
  onScanComplete?: (result: unknown) => void;
  onError?: (err: StreamError) => void;
}

export function useScanAgentStream({
  scanId: initialScanId,
  scanDbId,
  companyName,
  websiteUrl,
  businessType,
  onScanComplete,
  onError,
}: UseScanAgentStreamOptions) {
  const [agentStates, setAgentStates] = useState<Record<string, AgentLaneState>>(initialStates);
  const [isScanDone, setIsScanDone] = useState(false);
  const [streamError, setStreamError] = useState<StreamError | null>(null);
  const stepCounts = useRef<Record<string, number>>({});
  const doneRef = useRef(false);
  /** Lane currently running — used to attribute errors when backend omits agentKey. */
  const activeLaneRef = useRef<{ agentKey?: string; agentType?: string }>({});

  const patch = useCallback((key: string, update: Partial<AgentLaneState>) => {
    setAgentStates(prev => ({ ...prev, [key]: { ...prev[key], ...update } }));
  }, []);

  useLaneCreep(setAgentStates);

  const handleConnectionError = useCallback((err: StreamError) => {
    setStreamError(err);
    onError?.(err);
  }, [onError]);

  const markScanLanesDone = useCallback(() => {
    setAgentStates(prev => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (!SCAN_OWNED.has(k)) continue;
        if (next[k].phase !== 'done' && next[k].phase !== 'failed') {
          next[k] = { phase: 'done', progress: 1, message: next[k].message };
        }
      }
      return next;
    });
    setIsScanDone(true);
  }, []);

  const handleMessage = useCallback((rawData: string) => {
    const msg = parseStreamMessage(rawData);
    if (!msg) return;

    if (msg.type === 'connected') return;

    if (msg.type === 'scan-missing') {
      if (doneRef.current) return;
      scanIdRef.current = null;
      stepCounts.current = {};
      activeLaneRef.current = {};

      void (async () => {
        const persisted = await fetchPersistedScan(scanDbId);
        if (doneRef.current) return;

        if (persisted?.result) {
          doneRef.current = true;
          markScanLanesDone();
          onScanComplete?.(persisted.result);
          return;
        }

        doneRef.current = false;
        setAgentStates(initialStates());

        if (!websiteUrl) {
          const e: StreamError = { source: 'stream', message: 'Scan no longer exists. Please start a new scan.', retryable: false };
          setStreamError(e);
          onError?.(e);
          return;
        }
        console.warn('[scan stream] scan-missing — re-creating scan');
        void startRef.current();
      })();
      return;
    }

    if (msg.type === 'scan-complete') {
      if (doneRef.current) return;
      doneRef.current = true;
      if (msg.status === 'completed') {
        setAgentStates(prev => {
          const next = { ...prev };
          for (const k of Object.keys(next)) {
            if (!SCAN_OWNED.has(k)) continue;
            if (next[k].phase !== 'done' && next[k].phase !== 'failed') {
              next[k] = { phase: 'done', progress: 1, message: next[k].message };
            }
          }
          return next;
        });
        setIsScanDone(true);
        onScanComplete?.(msg.result);
      } else {
        const err = toStreamError(msg.error);
        if (!err.agentKey && !err.agentType) {
          err.agentKey = activeLaneRef.current.agentKey;
          err.agentType = activeLaneRef.current.agentType;
        }
        if (err.agentKey) patch(err.agentKey, { phase: 'failed', message: err.message });
        console.error('[scan stream] failed', err);
        setStreamError(err);
        onError?.(err);
      }
      return;
    }

    const agentType = msg.agentType as string | undefined;
    const event = msg.event as Record<string, unknown> | undefined;
    if (!event || !agentType) return;

    if (event.type === 'insight') return;

    const laneKey = AGENT_TYPE_MAP[agentType];
    if (!laneKey) return;

    switch (event.type as string) {
      case 'started': {
        const fallback = agentByKey(laneKey)?.runningLabel ?? 'Working…';
        activeLaneRef.current = { agentKey: laneKey, agentType };
        patch(laneKey, { phase: 'running', progress: 0.05, message: fallback });
        break;
      }
      case 'step': {
        const label = (event.label as string) ?? '';
        const detail = event.detail as Record<string, unknown> | undefined;
        activeLaneRef.current = { agentKey: laneKey, agentType };

        if (label === 'persona-ready' && detail) {
          const persona = detail.persona as Record<string, unknown> | undefined;
          const index = detail.index as number | undefined;
          const name = typeof persona?.name === 'string' ? persona.name : 'Unnamed persona';
          if (typeof index === 'number') {
            setAgentStates(prev => {
              const curr = prev[PERSONA_LANE_KEY] ?? { phase: 'queued', progress: 0, message: '' };
              const list = Array.isArray(curr.personas) ? [...curr.personas] : [];
              const existingIdx = list.findIndex(p => p.index === index);
              const row = { index, name, progress: 1, phase: 'done' as const };
              if (existingIdx >= 0) list[existingIdx] = row;
              else list.push(row);
              list.sort((a, b) => a.index - b.index);
              return { ...prev, [PERSONA_LANE_KEY]: { ...curr, phase: curr.phase === 'done' ? 'done' : 'running', personas: list } };
            });
          }
          break;
        }

        const steps = (stepCounts.current[laneKey] ?? 0) + 1;
        stepCounts.current[laneKey] = steps;
        const fallback = agentByKey(laneKey)?.runningLabel ?? '';
        const message = (event.message as string) || label || fallback;
        const { progress: computed, cap } = computeStepProgress(message, steps);
        const newIcon = deriveStepIcon(laneKey, label, detail);
        setAgentStates(prev => {
          const curr = prev[laneKey] ?? { phase: 'queued', progress: 0, message: '' };
          const existingIcons = curr.icons ?? [];
          const icons = newIcon && !existingIcons.some(i => i.label === newIcon!.label)
            ? [...existingIcons, newIcon]
            : existingIcons;
          return {
            ...prev,
            [laneKey]: {
              ...curr,
              phase: 'running',
              progress: Math.max(curr.progress, computed),
              message,
              cap: cap ?? curr.cap,
              icons,
              steps: appendSubStep(curr.steps, laneKey, steps, message),
            },
          };
        });
        break;
      }
      case 'completed':
        setAgentStates(prev => {
          const curr = prev[laneKey] ?? { phase: 'queued' as const, progress: 0, message: '' };
          return { ...prev, [laneKey]: { ...curr, phase: 'done', progress: 1, cap: undefined, steps: curr.steps?.map(s => ({ ...s, done: true })) } };
        });
        break;
      case 'failed':
        patch(laneKey, { phase: 'failed', message: (event.error as string) || 'Failed' });
        activeLaneRef.current = { agentKey: laneKey, agentType };
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scanIdRef comes from useScanConnection, which takes this callback, so it cannot be declared first
  }, [onScanComplete, onError, patch, websiteUrl, scanDbId, markScanLanesDone]);

  const { start, close, scanIdRef } = useScanConnection({
    initialScanId,
    websiteUrl,
    companyName,
    businessType,
    onMessage: handleMessage,
    onError: handleConnectionError,
  });

  const startRef = useRef(start);
  useEffect(() => { startRef.current = start; }, [start]);

  const resume = useCallback(() => {
    setStreamError(null);
    doneRef.current = false;
    void start();
  }, [start]);

  useEffect(() => {
    void start();
    return close;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { agentStates, isScanDone, streamError, resume };
}